import { useCallback, useEffect, useState } from 'react';
import { LiveKitRoom } from '@livekit/components-react';
import { useQueryClient } from '@tanstack/react-query';
import type { Room } from 'livekit-client';

import { StateCard } from '@/components/clinical';
import { useClient } from '@/features/clients/hooks';
import type { RiskProfile } from '@/features/clients/types/schemas';
import { useSession } from '@/features/sessions/hooks/useSession';
import { sessionsKeys } from '@/features/sessions/hooks/useSessions';
import { useUpdateSessionStatus } from '@/features/sessions/hooks/useUpdateSessionStatus';
import type { ConcernKey } from '@/features/sessions/constants/concerns';
import { sessionsService } from '@/features/sessions/services';
import { clinicalBreadcrumb } from '@/lib/telemetry/clinical';
import { toast } from '@/lib/toast';
import { useAuthStore } from '@/stores/authStore';

import { useCallStatus } from '../hooks/useCallStatus';
import { useEndCall } from '../hooks/useEndCall';
import { useJoinCall } from '../hooks/useJoinCall';
import { useKickParticipant } from '../hooks/useKickParticipant';
import { useMuteParticipant } from '../hooks/useMuteParticipant';
import { useStartRecording } from '../hooks/useStartRecording';
import { useStopRecording } from '../hooks/useStopRecording';
import { toEffectiveClass } from '../lib/effectiveClass';
import { runLeaveAndComplete } from '../lib/leaveAndComplete';
import type { MuteParticipantInput, KickParticipantInput } from '../services/videoCallService';

import { type SidePanelId } from './CallControlBar';
import { LeaveAndCompleteDialog } from './LeaveAndCompleteDialog';
import { MarkNoShowDialog } from './MarkNoShowDialog';
import { PreJoinScreen } from './PreJoinScreen';
import { VideoCallStage } from './VideoCallStage';

export interface VideoCallScreenProps {
  /** The session whose call to join. Caller is responsible for resolving
   *  this from the route (params, search params, etc.). */
  sessionId: string;
  /** Where to send the user when they leave pre-join, disconnect, or
   *  bail on a join error. Each surface (clinical, workspace) navigates
   *  back to its own session-detail URL. */
  onExit: () => void;
  /** Optional. Where the practitioner should land when the LiveKit room
   *  disconnects (call ended). Falls back to onExit. The post-session
   *  drawer in the workspace is the standard target. */
  onPostSessionRedirect?: () => void;
}

/**
 * Surface-agnostic video call screen — drives the pre-join → live call →
 * cleanup flow used by both the clinical surface (`/app/sessions/:id/call`)
 * and the workspace surface (`/admin/workspace/sessions/:id/call`). The
 * thin route wrappers in `src/routes/_staff-fullscreen/...` only have to
 * resolve `sessionId` from params and pass an `onExit` that navigates
 * back to their own detail URL.
 */
export function VideoCallScreen({
  sessionId,
  onExit,
  onPostSessionRedirect,
}: VideoCallScreenProps) {
  const [phase, setPhase] = useState<'pre-join' | 'live'>('pre-join');
  const [selectedDevices, setSelectedDevices] = useState<{
    cameraId?: string;
    micId?: string;
    audioOnly?: boolean;
    /** Initial publish state from the lobby. Default true on both. */
    cameraEnabled?: boolean;
    micEnabled?: boolean;
  }>({});
  // `isPaused` drives the Start/Stop toggle in the control bar — it's
  // a UI signal for which mutation button to render. The actual REC
  // indicator visibility is driven by LiveKit's Room.isRecording (see
  // useIsRecording in VideoCallStage), so we no longer need a separate
  // "has started" latch — egress state is shared across all participants.
  const [isPaused, setIsPaused] = useState(true);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [noShowDialogOpen, setNoShowDialogOpen] = useState(false);
  const [openPanel, setOpenPanel] = useState<SidePanelId | null>(null);
  const [localRoom, setLocalRoom] = useState<Room | null>(null);

  function handleTogglePanel(id: SidePanelId) {
    setOpenPanel((current) => (current === id ? null : id));
  }

  const callStatus = useCallStatus(sessionId);
  const sessionQuery = useSession(sessionId);
  const join = useJoinCall();
  const { mutate: joinCall } = join;

  const endCall = useEndCall();
  const updateStatus = useUpdateSessionStatus(sessionId);
  const queryClient = useQueryClient();
  const startRecording = useStartRecording();
  const stopRecording = useStopRecording();
  const muteParticipant = useMuteParticipant();
  const kickParticipant = useKickParticipant();

  // Risk-update controls inside Leave & complete: psy/chief_psy only.
  // Volunteers see the dialog but not the risk selector. Mirrors the BE
  // guard in sessionService.updateSessionStatus.
  const userRole = useAuthStore((s) => s.user?.role ?? null);
  const clientUserId = sessionQuery.data?.clientId ?? undefined;
  // No client (anonymous helpline / walk-in) means nothing to profile, so the
  // selector is hidden rather than demanding a risk for a client that isn't there.
  const canSetRisk =
    (userRole === 'psychologist' || userRole === 'chief_psychologist') && Boolean(clientUserId);
  // Volunteers (listeners) don't diagnose, so they don't fill concerns when
  // completing a call. Hide the picker for them; the BE gate exempts volunteers
  // too, so a concern-less completion is accepted.
  const requireConcerns = userRole !== 'volunteer';
  // The session carries the client's risk, so a psychologist behind on
  // availability sees it without the cadence-gated client read (which opens the
  // blocking modal). An older API omits the field; read the client then.
  const sessionRisk = sessionQuery.data?.clientRiskProfile;
  const clientQuery = useClient(canSetRisk && sessionRisk === undefined ? clientUserId : undefined);
  const currentRisk: RiskProfile | null =
    sessionRisk !== undefined ? sessionRisk : (clientQuery.data?.riskProfile ?? null);

  const mode: 'online' | 'helpline' =
    sessionQuery.data?.sessionMode === 'helpline' ? 'helpline' : 'online';

  // Fire join only when phase flips to 'live'.
  useEffect(() => {
    if (phase === 'live') {
      joinCall(sessionId);
    }
  }, [phase, joinCall, sessionId]);

  // Surface the backend's actual reason via toast and return to the session
  // detail page. Prior UX was a full-screen "Couldn't join" card with a
  // hardcoded JWT-misalignment message, which masked legitimate business
  // errors (e.g. "scheduled time has passed").
  useEffect(() => {
    if (!join.isError) return;
    const raw = join.error.message;
    toast.error(raw.length > 0 ? raw : "Couldn't join the call.");
    onExit();
  }, [join.isError, join.error, onExit]);

  const handleMediaDeviceFailure = useCallback((_failure: unknown, kind?: MediaDeviceKind) => {
    if (kind === 'videoinput') {
      toast.mediaDeviceError('camera');
    } else if (kind === 'audioinput') {
      toast.mediaDeviceError('mic');
    }
  }, []);

  function disconnectLocally() {
    void localRoom?.disconnect();
  }

  const effectiveClass = join.data ? toEffectiveClass(join.data.role) : 'client';

  function handleJoinFromPreJoin(devices: {
    cameraId?: string;
    micId?: string;
    audioOnly?: boolean;
    cameraEnabled?: boolean;
    micEnabled?: boolean;
  }) {
    setSelectedDevices(devices);
    setPhase('live');
  }

  /**
   * "Leave session" — disconnect locally only. The LiveKit room stays
   * alive so the practitioner can rejoin via /call/join, and the session
   * status stays whatever it was (`scheduled` / `in_progress`). For
   * clients this is also the right exit path: they leave, the room
   * persists for the practitioner.
   */
  function handleLeave() {
    disconnectLocally();
  }

  /**
   * "Leave & complete" — practitioner-only. Opens the confirm dialog,
   * which itself decides whether to surface the concerns picker (when
   * Session.concerns is empty). Cancelling the dialog leaves the call
   * untouched.
   */
  function handleLeaveAndCompleteRequest() {
    setCompleteDialogOpen(true);
  }

  /**
   * Caller from LeaveAndCompleteDialog. `concerns` carries either the
   * existing Session.concerns (already on the row) or the freshly picked
   * set the practitioner just typed in. `risk` is only set when the
   * practitioner explicitly changed the client's risk in the same dialog;
   * undefined leaves the value untouched. The PATCH /sessions/:id/status
   * call is what enforces the BE concerns gate; the subsequent endCall
   * tears down the LiveKit room (the BE UPDATE inside endRoom is a
   * no-op because we already flipped status to completed).
   *
   * If the row was already completed underneath us (scheduledEnd sweep on
   * an overrunning call) runLeaveAndComplete keeps the wrap-up via the
   * completed-only concerns endpoint and still ends the call, so the
   * practitioner is never stranded in a room whose session is closed.
   */
  async function performLeaveAndComplete(input: { concerns: ConcernKey[]; risk?: RiskProfile }) {
    clinicalBreadcrumb('call:end', { sessionId });
    await runLeaveAndComplete({
      input,
      updateStatus: (i) =>
        updateStatus.mutateAsync({
          status: 'completed',
          concerns: i.concerns,
          riskProfile: i.risk,
        }),
      updateConcerns: async (concerns) => {
        await sessionsService.updateConcerns(sessionId, concerns);
        void queryClient.invalidateQueries({ queryKey: sessionsKeys.detail(sessionId) });
      },
      endCall: () => endCall.mutateAsync(sessionId),
      onCallEndFailed: () => {
        toast.callEndFailed();
      },
      onWrapUpNotSaved: ({ what }) => {
        toast.wrapUpNotSaved({ what });
      },
    });
    setCompleteDialogOpen(false);
    disconnectLocally();
  }

  /**
   * "No-show" — practitioner-only. Opens the attestation dialog. Cancelling
   * it leaves the call untouched, same contract as Leave & complete.
   */
  function handleMarkNoShowRequest() {
    setNoShowDialogOpen(true);
  }

  /**
   * Caller from MarkNoShowDialog. Mirrors performLeaveAndComplete with the
   * concerns / risk arguments dropped: no_show is exempt from the BE concerns
   * gate, so the PATCH carries status alone. Same ordering — flip status
   * first, then tear the room down, then disconnect locally so LiveKit's
   * onDisconnected fires the post-session redirect.
   */
  async function performMarkNoShow() {
    clinicalBreadcrumb('call:end', { sessionId });
    await updateStatus.mutateAsync({ status: 'no_show' });
    try {
      await endCall.mutateAsync(sessionId);
    } catch {
      // Room teardown failure is non-fatal — the session is already no_show
      // server-side. Quiet toast, disconnect either way.
      toast.callEndFailed();
    }
    setNoShowDialogOpen(false);
    disconnectLocally();
  }

  function handlePauseRecording() {
    clinicalBreadcrumb('call:recording-stop', { sessionId });
    stopRecording.mutate(sessionId, {
      onSuccess: () => {
        setIsPaused(true);
      },
      onError: () => {
        toast.recordingStopFailed();
      },
    });
  }

  function handleResumeRecording() {
    clinicalBreadcrumb('call:recording-start', { sessionId });
    startRecording.mutate(sessionId, {
      onSuccess: () => {
        setIsPaused(false);
      },
      onError: () => {
        toast.recordingStartFailed();
      },
    });
  }

  function handleMuteParticipant(input: MuteParticipantInput) {
    clinicalBreadcrumb('call:participant-mute', { sessionId });
    muteParticipant.mutate(
      { sessionId, ...input },
      {
        onError: () => {
          toast.participantActionFailed();
        },
      },
    );
  }

  function handleKickParticipant(input: KickParticipantInput) {
    clinicalBreadcrumb('call:participant-kick', { sessionId });
    kickParticipant.mutate(
      { sessionId, ...input },
      {
        onError: () => {
          toast.participantActionFailed();
        },
      },
    );
  }

  // Wait for session data to resolve before rendering PreJoinScreen.
  // Without this, `mode` defaults to 'online' during the initial render
  // and flips to 'helpline' when sessionQuery resolves — causing a
  // visible camera preview flicker on cold-cache deep-link paths.
  if (phase === 'pre-join' && !sessionQuery.data) {
    return (
      <StateCard
        variant="empty"
        headline="Loading session…"
        description="Just a moment while we look up your session details."
      />
    );
  }

  // Pre-join phase renders PreJoinScreen.
  if (phase === 'pre-join') {
    return (
      <PreJoinScreen
        sessionId={sessionId}
        mode={mode}
        callStatus={callStatus.data}
        isStatusError={callStatus.isError}
        onRefreshStatus={() => {
          void callStatus.refetch();
        }}
        onJoin={handleJoinFromPreJoin}
        onCancel={onExit}
      />
    );
  }

  // Live phase — join mutation handling. On error the effect above toasts the
  // backend message and navigates back; render a transient empty state while
  // that navigation lands.
  if (join.isError || !join.data) {
    return (
      <StateCard
        variant="empty"
        headline="Joining the call…"
        description="Waiting for the room token."
      />
    );
  }

  // Honour the lobby toggles. cameraEnabled/micEnabled === false means the
  // user wants to join muted/blanked — passing `false` to LiveKitRoom skips
  // publishing that track entirely. They can flip it back on from the live
  // bottom bar once inside the room.
  const videoConstraint: boolean | { deviceId: string } =
    selectedDevices.audioOnly || mode === 'helpline' || selectedDevices.cameraEnabled === false
      ? false
      : selectedDevices.cameraId != null
        ? { deviceId: selectedDevices.cameraId }
        : true;

  const audioConstraint: boolean | { deviceId: string } =
    selectedDevices.micEnabled === false
      ? false
      : selectedDevices.micId != null
        ? { deviceId: selectedDevices.micId }
        : true;

  const envUrl = import.meta.env.VITE_LIVEKIT_URL as string | undefined;
  const serverUrl = envUrl != null && envUrl.length > 0 ? envUrl : join.data.livekitUrl;

  return (
    <>
      <LiveKitRoom
        token={join.data.token}
        serverUrl={serverUrl}
        connect
        video={videoConstraint}
        audio={audioConstraint}
        onDisconnected={onPostSessionRedirect ?? onExit}
        onMediaDeviceFailure={handleMediaDeviceFailure}
        data-lk-theme="default"
      >
        <VideoCallStage
          effectiveClass={effectiveClass}
          mode={mode}
          audioOnly={selectedDevices.audioOnly}
          isPaused={isPaused}
          openPanel={openPanel}
          isRecordingPending={startRecording.isPending || stopRecording.isPending}
          sessionNames={{
            clientName: sessionQuery.data?.clientName,
            practitionerName: sessionQuery.data?.practitionerName,
            practitionerKind: sessionQuery.data?.practitionerKind,
          }}
          sessionId={sessionId}
          actualStart={sessionQuery.data?.actualStart ?? null}
          clientUserId={sessionQuery.data?.clientId ?? null}
          practitionerUserId={sessionQuery.data?.practitionerId ?? null}
          onLeave={handleLeave}
          onLeaveAndComplete={
            effectiveClass === 'practitioner' ? handleLeaveAndCompleteRequest : undefined
          }
          onMarkNoShow={effectiveClass === 'practitioner' ? handleMarkNoShowRequest : undefined}
          onRoomReady={setLocalRoom}
          onStartRecording={effectiveClass === 'practitioner' ? handleResumeRecording : undefined}
          onStopRecording={effectiveClass === 'practitioner' ? handlePauseRecording : undefined}
          onTogglePanel={handleTogglePanel}
          onMuteParticipant={effectiveClass === 'practitioner' ? handleMuteParticipant : undefined}
          onKickParticipant={effectiveClass === 'practitioner' ? handleKickParticipant : undefined}
        />
      </LiveKitRoom>

      <LeaveAndCompleteDialog
        open={completeDialogOpen}
        onOpenChange={setCompleteDialogOpen}
        existingConcerns={sessionQuery.data?.concerns ?? []}
        requireConcerns={requireConcerns}
        isPending={updateStatus.isPending || endCall.isPending}
        canSetRisk={canSetRisk}
        currentRisk={currentRisk}
        coping={canSetRisk && clientUserId ? { clientId: clientUserId, sessionId } : undefined}
        onConfirm={performLeaveAndComplete}
      />

      <MarkNoShowDialog
        open={noShowDialogOpen}
        onOpenChange={setNoShowDialogOpen}
        isPending={updateStatus.isPending || endCall.isPending}
        onConfirm={performMarkNoShow}
      />
    </>
  );
}
